import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'

import { useGetQuotingPricesQuoteId as useGetQuotePricing } from 'utils/web_api'
import { type GetQuotingPricesQuoteIdWorkflowType as WorkflowType, type QuotingQuotePricing as QuotePricing, type QuotingQuoteQuoteType as QuoteType } from 'utils/web_api.schemas'

type QuotePricingProviderProps = {
  quoteId: string
  defaultQuoteType?: QuoteType
  workflowType: WorkflowType
}

type QuotePricingContextType = QuotePricingProviderProps & {
  quotePricing?: QuotePricing
  hasPricing?: boolean
  isPending: boolean
  pepmCost: number
  getTotalCost: (employeeCount: number) => number
  refetch: () => void
  quoteType?: QuoteType
  setQuoteType: (quoteType: QuoteType) => void
}

const QuotePricingContext = createContext<QuotePricingContextType>({
  quoteId: '',
  isPending: true,
  pepmCost: 0,
  getTotalCost: () => 0,
  workflowType: 'sales',
  refetch: () => {},
  setQuoteType: () => {},
})

export function useQuotePricing () {
  return useContext(QuotePricingContext)
}

export function QuotePricingProvider ({
  quoteId,
  defaultQuoteType,
  workflowType,
  children,
}: PropsWithChildren<QuotePricingProviderProps>) {
  const [quoteType, setQuoteType] = useState(defaultQuoteType)

  const { data: quotePricing, refetch, isPending } = useGetQuotePricing(
    quoteId,
    {
      workflow_type: workflowType,
    },
    { query: { enabled: !!quoteType && quoteType !== 'renewal' && workflowType !== 'renewal' } }
  )

  const hasPricing = useMemo(() => {
    if (!quotePricing) {
      return false
    }

    return Object.values(quotePricing).some(val => val != null)
  }, [quotePricing])

  const pepmCost = useMemo(() => {
    if (!quotePricing || !hasPricing) {
      return 0
    }

    const { base_compliance_price: _, ...rest } = quotePricing
    return Object.values(rest).reduce((sum, val) => sum + val, 0)
  }, [quotePricing, hasPricing])

  const getTotalCost = useCallback((employeeCount: number) => {
    return pepmCost * employeeCount + (quotePricing?.base_compliance_price ?? 0)
  }, [pepmCost, quotePricing])

  return (
    <QuotePricingContext.Provider
      value={{
        quoteId,
        quoteType,
        setQuoteType,
        quotePricing,
        workflowType,
        isPending,
        hasPricing,
        pepmCost,
        getTotalCost,
        refetch,
      }}
    >
      {children}
    </QuotePricingContext.Provider>
  )
}
